import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Clock, RefreshCw } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import DetailsToggle from '../components/DetailsToggle';
import { useDetails } from '../hooks/useDetails';
import { navLabel } from '../utils/navigation';
import InfoTip from '../components/InfoTip';
import MonthPicker from '../components/MonthPicker';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { shiftMonth } from '../utils/urlParams';
import { usePeriodParams } from '../hooks/usePeriodParams';
import { useTabParam } from '../hooks/useTabParam';
import FlowTabs from '../components/flow/FlowTabs';
import EffortScoreCard from '../components/effort/EffortScoreCard';
import FixList from '../components/effort/FixList';
import GapsView from '../components/effort/GapsView';
import TrendStrip from '../components/effort/TrendStrip';
import { EffortSummary, FixPerson, FixRow, tagTone } from '../components/effort/types';
import * as fmt from '../lib/format';

const CARD = 'rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400';
const CAPTION = 'text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500';
const ICON_BUTTON =
  'rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-white hover:text-gray-900 active:scale-[0.96] disabled:opacity-30 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';
const PAGE = 200;
type TabId = 'logging' | 'gaps';

// Effort: how time is logged, and where it is missing. The logging checks follow Hygiene's
// visibility (a leader does not see them); the gaps are delivery health that every role may read, so
// a leader sees the Gaps view only.
export default function EffortPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canSeeChecks = user?.role !== 'leader';

  // The month, the project, "only mine" and the tab live in the address, so a reload or a copied link opens
  // the same view (and a link from Compliance can open a month and a project).
  const { period, current, isCurrent, setPeriod, projectKey, setProjectKey, mine, setMine } = usePeriodParams();
  const [tab, setTab] = useTabParam<TabId>(canSeeChecks ? ['logging', 'gaps'] : ['gaps'], canSeeChecks ? 'logging' : 'gaps');
  const [refreshKey, setRefreshKey] = useState(0);
  // Headline first: the share, what needs a fix and the entries; the trend and the per-project cards are one press away (?details=1).
  const [details, setDetails] = useDetails();

  const [summary, setSummary] = useState<EffortSummary | null>(null);
  const [summaryMeta, setSummaryMeta] = useState<{ unlinked?: boolean; tagging_from?: string }>({});
  const [loadingSummary, setLoadingSummary] = useState(canSeeChecks);

  const [rows, setRows] = useState<FixRow[]>([]);
  const [people, setPeople] = useState<FixPerson[]>([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(PAGE);
  const [loadingRows, setLoadingRows] = useState(true);

  const [error, setError] = useState<string | null>(null);
  const useMine = !isAdmin && mine;
  const showLogging = canSeeChecks && tab === 'logging';

  useEffect(() => {
    if (!canSeeChecks) return;
    let cancelled = false;
    setLoadingSummary(true);
    apiService
      .getEffortSummary({ period, mine: useMine })
      .then((r) => {
        if (cancelled) return;
        if (r.success) {
          setSummary(r.data);
          setSummaryMeta(r.meta ?? {});
          setError(null);
        } else setError(r.error || 'Could not load the summary');
      })
      .catch((e: any) => !cancelled && setError(e?.response?.data?.error || 'Could not load the summary'))
      .finally(() => !cancelled && setLoadingSummary(false));
    return () => {
      cancelled = true;
    };
  }, [period, useMine, canSeeChecks, refreshKey]);

  useEffect(() => {
    if (!showLogging) return;
    let cancelled = false;
    setLoadingRows(true);
    apiService
      .getEffortFixes({ period, project_key: projectKey ?? undefined, mine: useMine, limit })
      .then((r) => {
        if (cancelled) return;
        if (r.success) {
          setRows(r.data ?? []);
          setPeople(r.people ?? []);
          setTotal(r.meta?.total ?? 0);
        }
      })
      .catch((e: any) => !cancelled && setError(e?.response?.data?.error || 'Could not load the entries'))
      .finally(() => !cancelled && setLoadingRows(false));
    return () => {
      cancelled = true;
    };
  }, [period, projectKey, useMine, limit, showLogging, refreshKey]);

  const changePeriod = (next: string) => {
    setPeriod(next);
    setLimit(PAGE);
  };
  const changeProject = (key: string | null) => {
    setProjectKey(key);
    setLimit(PAGE);
  };

  const periodIsCurrent = isCurrent;
  const overall = summary?.overall ?? null;
  const projects = summary?.projects ?? [];
  const taggingMonth = (summaryMeta.tagging_from ?? '').slice(0, 7);
  const notJudged = !!summary && !summary.tagging_started;
  const refreshing = loadingSummary || loadingRows;

  const emptyFixes = useMemo(() => {
    if (useMine) return summaryMeta.unlinked ? 'Your login is not matched to a Jira account yet.' : `All your entries in ${fmt.month(period)} have one standard tag.`;
    if ((overall?.entries ?? 0) === 0) return `No time was logged in ${fmt.month(period)} within your scope.`;
    return `Every entry in ${fmt.month(period)} has one standard tag.`;
  }, [useMine, summaryMeta.unlinked, period, overall]);

  const tabs = canSeeChecks ? [{ id: 'logging', label: 'Entries to fix', count: overall?.need_fix, tip: 'effort.logging' as const }, { id: 'gaps', label: 'No time on open work', tip: 'effort.gaps' as const }] : [];

  const refreshButton = (
    <button type="button" onClick={() => setRefreshKey((n) => n + 1)} aria-label="Refresh" className={ICON_BUTTON}>
      <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin motion-reduce:animate-none' : ''}`} />
    </button>
  );
  const projectChip = projectKey && (
    <button type="button" onClick={() => changeProject(null)} className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-800 ring-1 ring-inset ring-blue-500 transition-colors active:scale-[0.97] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
      {projectKey}
      <span aria-hidden>×</span>
      <span className="sr-only">Clear the project filter</span>
    </button>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100/60">
      <PageHeader title={navLabel('/effort')} subtitle="How time is logged, and where it is missing" icon={Clock} />
      <div className="p-4 sm:p-6">
        <div className="mx-auto max-w-7xl">
          {canSeeChecks && <FlowTabs tabs={tabs} active={tab} onChange={(id) => setTab(id as TabId)} label="Time logging views" idPrefix="effort" />}

          {error && <div className="mb-4 rounded-xl bg-rose-50 px-4 py-2.5 text-sm text-rose-700 ring-1 ring-rose-200" role="alert">{error}</div>}

          {showLogging && (
            <div role="tabpanel" id="effort-panel-logging" aria-labelledby="effort-tab-logging">
              {/* Period and filters */}
              <div className="mb-5 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-1">
                  <button type="button" aria-label="Previous month" onClick={() => changePeriod(shiftMonth(period, -1))} className={ICON_BUTTON}>
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <MonthPicker value={period} max={current} onChange={changePeriod} />
                  <button type="button" aria-label="Next month" disabled={periodIsCurrent} onClick={() => changePeriod(shiftMonth(period, 1))} className={ICON_BUTTON}>
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
                {!isAdmin && (
                  <label className="flex items-center gap-2 text-sm text-gray-600">
                    <input type="checkbox" checked={mine} onChange={(e) => { setMine(e.target.checked); setLimit(PAGE); }} className="rounded border-gray-300 text-blue-600 focus:ring-blue-200" />
                    Only mine
                  </label>
                )}
                {projectChip}
                <div className="ml-auto">{refreshButton}</div>
              </div>

              {!isAdmin && summaryMeta.unlinked && (
                <div className="mb-4 rounded-xl bg-amber-50 px-4 py-2.5 text-sm text-amber-900 ring-1 ring-amber-200">
                  Your login is not matched to a Jira account yet, so your own entries cannot be shown. Ask an admin to link it.
                </div>
              )}

              {notJudged ? (
                <div className={`${CARD} px-6 py-12 text-center`}>
                  <p className="text-sm text-gray-700">
                    The tagging standard started in {taggingMonth ? fmt.month(taggingMonth) : 'July 2026'}, so {fmt.month(period)} is not judged.
                  </p>
                  {taggingMonth && (
                    <button
                      type="button"
                      onClick={() => changePeriod(taggingMonth)}
                      className="mt-4 rounded-xl px-4 py-2 text-sm font-medium text-blue-700 ring-1 ring-inset ring-blue-200 transition-[background-color,transform] hover:bg-blue-50/70 active:scale-[0.98] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    >
                      Go to {fmt.month(taggingMonth)}
                    </button>
                  )}
                </div>
              ) : (
                <>
                  {/* The month at a glance */}
                  {loadingSummary && !summary ? (
                    <div className={`${CARD} mb-6 h-36 animate-pulse motion-reduce:animate-none`} role="status" aria-label="Loading summary" />
                  ) : overall ? (
                    <section className={`${CARD} mb-6 grid gap-6 p-5 md:grid-cols-[minmax(0,1fr)_auto] md:items-center`} aria-label="Entries with one standard tag">
                      <div>
                        <div className="flex items-center">
                          <h2 className={CAPTION}>Entries with one standard tag</h2>
                          <InfoTip id="effort.valid-share" />
                        </div>
                        <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                          <span className={`text-4xl font-semibold tabular-nums tracking-tight ${tagTone(overall.valid_pct)}`}>{fmt.percent(overall.valid_pct)}</span>
                          <span className="text-sm text-gray-600">
                            {fmt.count(overall.valid)} of {fmt.count(overall.entries)} {overall.entries === 1 ? 'entry' : 'entries'} in {fmt.month(period)}
                          </span>
                        </div>
                        <p className="mt-2 text-sm text-gray-600">
                          {overall.need_fix === 0
                            ? 'Nothing to fix.'
                            : `${fmt.count(overall.need_fix)} to fix (${fmt.hours(overall.need_fix_hours)}): ${[
                                overall.by_problem.no_tag > 0 ? `${fmt.count(overall.by_problem.no_tag)} with no tag` : null,
                                overall.by_problem.unknown_tag > 0 ? `${fmt.count(overall.by_problem.unknown_tag)} with an unknown tag` : null,
                                overall.by_problem.two_tags > 0 ? `${fmt.count(overall.by_problem.two_tags)} with two tags` : null,
                              ]
                                .filter(Boolean)
                                .join(' · ')}.`}
                        </p>
                        {(projects.length > 0 || (summary?.trend?.length ?? 0) > 0) && (
                          <div className="mt-4">
                            <DetailsToggle open={details} onChange={setDetails} showLabel="Show by project and trend" hideLabel="Hide by project and trend" />
                          </div>
                        )}
                      </div>
                      {details && <TrendStrip points={summary?.trend ?? []} current={period} />}
                    </section>
                  ) : null}

                  {/* Per project: the filter for the list below */}
                  {details && projects.length > 0 && (
                    <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
                      {projects.map((p) => (
                        <EffortScoreCard key={p.project_key} row={p} selected={projectKey === p.project_key} onSelect={changeProject} />
                      ))}
                    </div>
                  )}

                  <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                    <div className="flex items-center">
                      <h2 className="text-sm font-semibold text-gray-900">
                        Entries to fix{projectKey ? ` · ${projectKey}` : ''}{total > 0 ? ` · ${fmt.count(total)} ${total === 1 ? 'entry' : 'entries'}` : ''}
                      </h2>
                      <InfoTip id="effort.standard-tag" />
                    </div>
                    <div className="text-xs text-gray-500">Grouped by who logged the time · edit the entry in Jira</div>
                  </div>
                  <FixList rows={rows} people={people} loading={loadingRows && rows.length === 0} emptyText={emptyFixes} />
                  {total > rows.length && (
                    <div className="mt-3 text-center">
                      <button type="button" onClick={() => setLimit((n) => n + PAGE)} className="rounded-xl px-4 py-2 text-sm font-medium text-blue-700 transition-[background-color,transform] hover:bg-blue-50/70 active:scale-[0.98] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                        Show {Math.min(PAGE, total - rows.length)} more <span className="text-gray-500">· {total - rows.length} left</span>
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {tab === 'gaps' && (
            <div role={canSeeChecks ? 'tabpanel' : undefined} id="effort-panel-gaps" aria-labelledby={canSeeChecks ? 'effort-tab-gaps' : undefined}>
              <div className="mb-4 flex flex-wrap items-center gap-3">
                {!canSeeChecks && (
                  <div className="flex items-center">
                    <h2 className="text-sm font-semibold text-gray-900">Where time is missing</h2>
                    <InfoTip id="effort.gaps" />
                  </div>
                )}
                {projectChip}
                <div className="ml-auto">{refreshButton}</div>
              </div>
              <GapsView projectKey={projectKey} onProject={changeProject} refreshKey={refreshKey} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
